import React, { useState, useEffect, useRef } from 'react';
import medagricycleLogo from '../aseets/images/medagricycle-logo-pro.png';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../contexts/SettingsContext';
import './Navbar.css';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, isLoading } = useSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const { t } = useTranslation();

  // Valeurs par défaut si les paramètres sont en cours de chargement
  const contactEmail = isLoading ? 'info@medagricycle.eu' : (settings.email || 'info@medagricycle.eu');
  const contactPhone = isLoading ? '+33 1 23 45 67 89' : (settings.phone || '+33 1 23 45 67 89');
  const facebookUrl = settings.facebook || '#';
  const twitterUrl = settings.twitter || '#';
  const linkedinUrl = settings.linkedin || '#';
  const instagramUrl = settings.instagram || '#';
  const youtubeUrl = settings.youtube || '#';

  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const [showToTop, setShowToTop] = useState(false);
  const lastYRef = useRef(0);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    // Vérifier si l'utilisateur est connecté
    const checkLoginStatus = () => {
      const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
      const name = localStorage.getItem('userName') || 'Admin';
      setIsLoggedIn(loggedIn);
      setUserName(name);
    };

    checkLoginStatus();
    
    // Écouter les changements de localStorage
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Vérifier périodiquement (pour les changements dans le même onglet)
    const interval = setInterval(checkLoginStatus, 1000);

    // Fermer le dropdown admin si on clique en dehors
    const handleClickOutside = (event) => {
      if (adminDropdownOpen && !event.target.closest('.admin-dropdown-container')) {
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setIsScrolled(y > 10);

      // Contrôler la visibilité du bouton toTop - apparaît après 50px de scroll sur mobile, 200px sur desktop
      const isMobile = window.innerWidth <= 1360;
      setShowToTop(y > (isMobile ? 50 : 200));

      const goingDown = y > lastYRef.current;
      const doc = document.documentElement;
      const maxScroll = Math.max(doc.scrollHeight - window.innerHeight, 1);
      const progress = y / maxScroll;
      const reachedBottom = window.innerHeight + y >= (doc.scrollHeight - 2) || progress >= 0.8;

      setAtBottom(reachedBottom);
      setIsHidden(goingDown && y > 120 && !reachedBottom);

      lastYRef.current = y;
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(interval);
    };
  }, [adminDropdownOpen]);

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    setIsLoggedIn(false);
    setAdminDropdownOpen(false);
    navigate('/');
  };

  const headerClassName = [
    'jet-theme-core-location jet-theme-core-location--header-location',
    isScrolled ? 'navbar--scrolled' : '',
    isHidden ? 'navbar--hidden' : '',
    atBottom ? 'navbar--at-bottom' : ''
  ].join(' ').trim();

  return (
    <>
      {/* Barre des réseaux sociaux */}
      <div className="social-bar">
        <div className="social-bar-container">
          <div className="social-links">
            {facebookUrl && facebookUrl !== '#' && (
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="social-link facebook">
              <i className="fab fa-facebook-f"></i>
            </a>
            )}
            {twitterUrl && twitterUrl !== '#' && (
              <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="social-link twitter">
              <i className="fab fa-twitter"></i>
            </a>
            )}
            {linkedinUrl && linkedinUrl !== '#' && (
              <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="social-link linkedin">
              <i className="fab fa-linkedin-in"></i>
            </a>
            )}
            {instagramUrl && instagramUrl !== '#' && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="social-link instagram">
              <i className="fab fa-instagram"></i>
            </a>
            )}
            {youtubeUrl && youtubeUrl !== '#' && (
              <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" className="social-link youtube">
                <i className="fab fa-youtube"></i>
              </a>
            )}
          </div>
          <div className="contact-info">
            <span className="contact-item">
              <i className="fas fa-envelope"></i>
              {contactEmail}
            </span>
            {contactPhone && (
            <span className="contact-item">
              <i className="fas fa-phone"></i>
                {contactPhone}
            </span>
            )}
          </div>
        </div>
      </div>

      <header id="jet-theme-core-header" className={headerClassName}>
        <div className="jet-theme-core-location__inner">
          <div data-elementor-type="jet_header" data-elementor-id="4647" className="elementor elementor-4647">
            <section className="elementor-section elementor-top-section elementor-element elementor-element-081d1f0 elementor-hidden-phone elementor-hidden-tablet elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="081d1f0" data-element_type="section">
              <div className="elementor-container elementor-column-gap-default">
                <div className="elementor-column elementor-col-33 elementor-top-column elementor-element elementor-element-857c7df" data-id="857c7df" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-46e5919 elementor-widget elementor-widget-jet-logo" data-id="46e5919" data-element_type="widget" data-widget_type="jet-logo.default">
                      <div className="elementor-widget-container">
                        <div className="elementor-jet-logo jet-blocks">
                          <div className="jet-logo jet-logo-type-image jet-logo-display-block navbar-logo-slot">
                            <Link to="/" className="jet-logo__link">
                              <img src={medagricycleLogo} className="jet-logo__img navbar-logo-img" alt="MedAgriCycle" width={100} height={100} decoding="sync" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="elementor-column elementor-col-33 elementor-top-column elementor-element elementor-element-ce6b901" data-id="ce6b901" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-54cf21b jet-nav-tablet-align-center jet-nav-align-center jet-nav-mobile-align-flex-start elementor-widget elementor-widget-jet-nav-menu" data-id="54cf21b" data-element_type="widget" data-widget_type="jet-nav-menu.default">
                      <div className="elementor-widget-container">
                        <nav className="jet-nav-wrap m-layout-mobile jet-mobile-menu jet-mobile-menu--default" data-mobile-trigger-device="mobile" data-mobile-layout="default">
                          <div className="jet-nav__mobile-trigger jet-nav-mobile-trigger-align-center">
                            <span className="jet-nav__mobile-trigger-open jet-blocks-icon">
                              <i aria-hidden="true" className="fas fa-bars"></i>
                            </span>
                            <span className="jet-nav__mobile-trigger-close jet-blocks-icon">
                              <i aria-hidden="true" className="fas fa-times"></i>
                            </span>
                          </div>
                          
                          <div className="menu-menu-container">
                            <div className="jet-nav m-layout-mobile jet-nav--horizontal">
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <NavLink to="/" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/') ? '#96ca2d' : undefined }}>{t('navbar.home')}</span>
                                </NavLink>
                              </div>
                            
                               <div className={`menu-item menu-item-has-children jet-nav__item ${projectDropdownOpen ? 'jet-nav-hover' : ''}`} style={{ position: 'relative' }}>
<div className="menu-item-link menu-item-link-depth-0 menu-item-link-top" 
   onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
   style={{ cursor: 'pointer' }}>
<span className="jet-nav-link-text">{t('navbar.project')}</span>
<div className="jet-nav-arrow"><i aria-hidden="true" className={`fa ${projectDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'}`} style={{ 
  transform: projectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease'
}}></i>
</div></div>
<div className="jet-nav__sub jet-nav-depth-0">
<div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5734 jet-nav__item jet-nav-item-sub">
  <Link to="/about" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">{t('navbar.about')}</span></Link></div>

	<div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5734 jet-nav__item jet-nav-item-sub">
    <Link to="/workpackages" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">{t('navbar.workPackages')}</span></Link></div>
	<div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5734 jet-nav__item jet-nav-item-sub">
    <Link to="/deliverables" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">{t('navbar.deliverables')}</span></Link></div>
	<div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5733 jet-nav__item jet-nav-item-sub">
    <Link to="/partners" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">{t('navbar.partners')}</span></Link></div>

  </div> </div>
  {/*
   <div className={`menu-item menu-item-has-children jet-nav__item ${resourcesDropdownOpen ? 'jet-nav-hover' : ''}`} style={{ position: 'relative' }}>
<div className="menu-item-link menu-item-link-depth-0 menu-item-link-top" 
   onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
   style={{ cursor: 'pointer' }}>
<span className="jet-nav-link-text">Resources</span>
<div className="jet-nav-arrow"><i aria-hidden="true" className={`fa ${resourcesDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'}`} style={{ 
  transform: resourcesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease'
}}></i>
</div></div>

<div className="jet-nav__sub jet-nav-depth-0">
	<div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5734 jet-nav__item jet-nav-item-sub">
    <Link to="/materials" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">Materials</span></Link></div>
	<div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5733 jet-nav__item jet-nav-item-sub">
    <Link to="/practice-abstracts" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">Practice Abstracts</span></Link></div>
    <div class="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item-5733 jet-nav__item jet-nav-item-sub">
    <Link to="/newsletter" class="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span class="jet-nav-link-text">Newsletter</span></Link></div>
  </div> </div>*/}
                          
                              
                          
                              
                              
                              
                           
                             <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/ressources" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={scrollToTop} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/ressources') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/ressources') ? '#96ca2d' : undefined }}>{t('navbar.resources')}</span>
                                </Link>
                              </div>
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/events" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={scrollToTop} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/events') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/events') ? '#96ca2d' : undefined }}>{t('navbar.events')}</span>
                                </Link>
                              </div>
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/news" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={scrollToTop} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/news') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/news') ? '#96ca2d' : undefined }}>{t('navbar.news')}</span>
                                </Link>
                              </div>
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/contact" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={scrollToTop} style={{ color: location.pathname === '/contact' ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: location.pathname === '/contact' ? '#96ca2d' : undefined }}>{t('navbar.contact')}</span>
                                </Link>
                              </div>
                              
                              
                            </div>
                          </div>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
                
				<div className="elementor-column elementor-col-33 elementor-top-column elementor-element elementor-element-b06cc89" data-id="b06cc89" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-ee8f379 elementor-shape-circle e-grid-align-mobile-center e-grid-align-right elementor-widget elementor-widget-social-icons" data-id="ee8f379" data-element_type="widget" data-widget_type="social-icons.default">
                      <div className="elementor-widget-container">                        
                          <LanguageSwitcher />

                        <div className="elementor-social-icons-wrapper elementor-grid" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                          {isLoggedIn ? (
                            <div className="admin-dropdown-container" style={{ position: 'relative', marginLeft: '12px' }}>
                              <button
                                onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '33px',
                                  borderRadius: '8px',
                                  background: '#96ca2d',
                                  color: '#fff',
                                  border: 'none',
                                  transition: 'all 0.3s ease',
                                  cursor: 'pointer',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#7fb069';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(150, 202, 45, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#96ca2d';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                aria-label="Admin"
                              >
                                <i className="fas fa-user" style={{ fontSize: '1rem' }} />
                              </button>
                              
                              {adminDropdownOpen && (
                                <div style={{
                                  position: 'absolute',
                                  top: 'calc(100% + 8px)',
                                  right: 0,
                                  background: '#ffffff',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  minWidth: '180px',
                                  zIndex: 1000,
                                  overflow: 'hidden'
                                }}
                                onMouseLeave={() => setAdminDropdownOpen(false)}
                                >
                                  <Link
                                    to="/admin/dashboard"
                                    onClick={() => setAdminDropdownOpen(false)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '12px 16px',
                                      color: '#374151',
                                      textDecoration: 'none',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s ease',
                                      borderBottom: '1px solid #f3f4f6'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#f9fafb';
                                      e.currentTarget.style.color = '#96ca2d';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.color = '#374151';
                                    }}
                                  >
                                    <i className="fas fa-tachometer-alt" style={{ fontSize: '14px', width: '16px' }} />
                                    <span>{t('navbar.dashboard')}</span>
                                  </Link>
                                  <button
                                    onClick={handleLogout}
                                    style={{
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '12px 16px',
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#dc2626',
                                      textDecoration: 'none',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      textAlign: 'left',
                                      textTransform: 'none'

                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#fef2f2';
                                      e.currentTarget.style.color = '#991b1b';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.color = '#dc2626';
                                    }}
                                  >
                                    <i className="fas fa-sign-out-alt" style={{ fontSize: '14px', width: '16px' }} />
                                    <span>{t('navbar.logout')}</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <Link
                            to="/login"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 14px',
                                borderRadius: '6px',
                              background: '#96ca2d',
                              color: '#fff',
                              textDecoration: 'none',
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease',
                              marginLeft: '12px',
                                textTransform: 'uppercase'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fff';
                              e.currentTarget.style.color = '#96ca2d';
                              e.currentTarget.style.border = '1px solid #96ca2d';
                              e.currentTarget.style.transform = 'translateX(3px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#96ca2d';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.border = 'none';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                            aria-label="Connexion"
                          >
                              <span>{t('navbar.login', 'Connexion')}</span>
                                </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Version mobile */}
            <section className="elementor-section elementor-top-section elementor-element elementor-element-d26620f elementor-hidden-desktop  elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="d26620f" data-element_type="section">
              <div className="elementor-container elementor-column-gap-default">
                <div className="elementor-column elementor-col-33 elementor-top-column elementor-element elementor-element-55294aa" data-id="55294aa" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-15c6cf2 elementor-widget elementor-widget-jet-logo" data-id="15c6cf2" data-element_type="widget" data-widget_type="jet-logo.default">
                      <div className="elementor-widget-container">
                        <div className="elementor-jet-logo jet-blocks">
                          <div className="jet-logo jet-logo-type-image jet-logo-display-block navbar-logo-slot">
                            <Link to="/" className="jet-logo__link">
                              <img src={medagricycleLogo} className="jet-logo__img navbar-logo-img" alt="MedAgriCycle" width={100} height={100} decoding="sync" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="elementor-column elementor-col-33 elementor-top-column elementor-element elementor-element-6f81605" data-id="6f81605" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-93578dd jet-nav-tablet-align-center jet-nav-align-center jet-nav-mobile-align-flex-start elementor-widget elementor-widget-jet-nav-menu" data-id="93578dd" data-element_type="widget" data-widget_type="jet-nav-menu.default">
                      <div className="elementor-widget-container">
                        <nav className={`jet-nav-wrap m-layout-mobile jet-mobile-menu jet-mobile-menu--right-side ${menuOpen ? 'jet-mobile-menu-active' : ''}`} data-mobile-trigger-device="mobile" data-mobile_layout="right-side">
                          <div 
                            className="jet-nav__mobile-trigger jet-nav-mobile-trigger-align-center"
                            onClick={() => setMenuOpen(!menuOpen)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span className={`jet-nav__mobile-trigger-open jet-blocks-icon ${menuOpen ? 'jet-nav-hidden' : ''}`}>
                              <i aria-hidden="true" className="fas fa-bars"></i>
                            </span>
                            <span className={`jet-nav__mobile-trigger-close jet-blocks-icon ${!menuOpen ? 'jet-nav-hidden' : ''}`}>
                              <i aria-hidden="true" className="fas fa-times"></i>
                            </span>
                          </div>
                          
                          <div className="menu-menu-container">
                            <div className="jet-nav m-layout-mobile jet-nav--horizontal">
                            <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={() => { scrollToTop(); setMenuOpen(false); }} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/') ? '#96ca2d' : undefined }}>{t('navbar.home')}</span>
                                </Link>
                              </div>
                              
                              <div className={`menu-item menu-item-has-children jet-nav__item ${projectDropdownOpen ? 'jet-nav-hover' : ''}`} style={{ position: 'relative' }}>
<div className="menu-item-link menu-item-link-depth-0 menu-item-link-top" 
   onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                                  style={{ cursor: 'pointer', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
<span className="jet-nav-link-text">{t('navbar.project')}</span>
                                  <div className="jet-nav-arrow">
                                    <i aria-hidden="true" className={`fa ${projectDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'}`} style={{ 
  transform: projectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease'
}}></i>
                                  </div>
                                </div>
                                {projectDropdownOpen && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '20px',
                                    right: '20px',
                                    background: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    minWidth: '180px',
                                    zIndex: 1000,
                                    overflow: 'hidden',
                                    marginTop: '8px'
                                  }}
                                  onMouseLeave={() => setProjectDropdownOpen(false)}
                                  >
                                    <Link
                                      to="/about"
                                      onClick={() => {
                                        scrollToTop();
                                        setMenuOpen(false);
                                        setProjectDropdownOpen(false);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '12px 16px',
                                        color: '#374151',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        borderBottom: '1px solid #f3f4f6'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f9fafb';
                                        e.currentTarget.style.color = '#96ca2d';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#374151';
                                      }}
                                    >
                                      <i className="fas fa-info-circle" style={{ fontSize: '14px', width: '16px' }} />
                                      <span>{t('navbar.about')}</span>
                                    </Link>
                                    <Link
                                      to="/workpackages"
                                      onClick={() => {
                                        scrollToTop();
                                        setMenuOpen(false);
                                        setProjectDropdownOpen(false);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '12px 16px',
                                        color: '#374151',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        borderBottom: '1px solid #f3f4f6'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f9fafb';
                                        e.currentTarget.style.color = '#96ca2d';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#374151';
                                      }}
                                    >
                                      <i className="fas fa-folder-open" style={{ fontSize: '14px', width: '16px' }} />
                                      <span>{t('navbar.workPackages')}</span>
                                    </Link>
                                    <Link
                                      to="/deliverables"
                                      onClick={() => {
                                        scrollToTop();
                                        setMenuOpen(false);
                                        setProjectDropdownOpen(false);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '12px 16px',
                                        color: '#374151',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        borderBottom: '1px solid #f3f4f6'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f9fafb';
                                        e.currentTarget.style.color = '#96ca2d';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#374151';
                                      }}
                                    >
                                      <i className="fas fa-file-alt" style={{ fontSize: '14px', width: '16px' }} />
                                      <span>{t('navbar.deliverables')}</span>
                                    </Link>
                                    <Link
                                      to="/partners"
                                      onClick={() => {
                                        scrollToTop();
                                        setMenuOpen(false);
                                        setProjectDropdownOpen(false);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '12px 16px',
                                        color: '#374151',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f9fafb';
                                        e.currentTarget.style.color = '#96ca2d';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#374151';
                                      }}
                                    >
                                      <i className="fas fa-handshake" style={{ fontSize: '14px', width: '16px' }} />
                                      <span>{t('navbar.partners')}</span>
                                    </Link>
                                  </div>
                                )}
                              </div>
  {/*
   <div className={`menu-item menu-item-has-children jet-nav__item ${resourcesDropdownOpen ? 'jet-nav-hover' : ''}`} style={{ position: 'relative' }}>
<div className="menu-item-link menu-item-link-depth-0 menu-item-link-top" 
   onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
   style={{ cursor: 'pointer' }}>
<span className="jet-nav-link-text">Resources</span>
<div className="jet-nav-arrow"><i aria-hidden="true" className={`fa ${resourcesDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'}`} style={{ 
  transform: resourcesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease'
}}></i>
</div></div>
<div className="jet-nav__sub jet-nav-depth-0" style={{
  display: resourcesDropdownOpen ? 'block' : 'none'
}}>
	<div className="menu-item jet-nav__item jet-nav-item-sub">
    <Link to="/materials" className="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span className="jet-nav-link-text">Materials</span></Link></div>
	<div className="menu-item jet-nav__item jet-nav-item-sub">
    <Link to="/practice-abstracts" className="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span className="jet-nav-link-text">Practice Abstracts</span></Link></div>
    <div className="menu-item jet-nav__item jet-nav-item-sub">
    <Link to="/newsletter" className="menu-item-link menu-item-link-depth-1 menu-item-link-sub"><span className="jet-nav-link-text">Newsletter</span></Link></div>
  </div> </div>*/}
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/ressources" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={() => { scrollToTop(); setMenuOpen(false); }} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/ressources') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/ressources') ? '#96ca2d' : undefined }}>{t('navbar.resources')}</span>
                                </Link>
                              </div>
                              
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/events" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={() => { scrollToTop(); setMenuOpen(false); }} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/events') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/events') ? '#96ca2d' : undefined }}>{t('navbar.events')}</span>
                                </Link>
                              </div>
                              
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/news" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={() => { scrollToTop(); setMenuOpen(false); }} style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/news') ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: (typeof window !== 'undefined' && window.location.pathname === '/news') ? '#96ca2d' : undefined }}>{t('navbar.news')}</span>
                                </Link>
                              </div>
                              
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/contact" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={() => { scrollToTop(); setMenuOpen(false); }} style={{ color: location.pathname === '/contact' ? '#96ca2d' : undefined }}>
                                  <span className="jet-nav-link-text" style={{ color: location.pathname === '/contact' ? '#96ca2d' : undefined }}>{t('navbar.contact')}</span>
                                </Link>
                              </div>
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <div className="menu-item-link menu-item-link-depth-0 menu-item-link-top">
                                  <span className="jet-nav-link-text"><LanguageSwitcher /></span>
                                </div>
                              </div>
                              
                              {/* Bouton Admin/Connexion mobile */}
                              {isLoggedIn ? (
                                <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item" style={{ position: 'relative' }}>
                                  <div className="admin-dropdown-container" style={{ position: 'relative', width: '100%', padding: '12px 0px' }}>
                                    <button
                                      onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '40px',
                                        height: '33px',
                                        borderRadius: '8px',
                                        background: '#96ca2d',
                                        color: '#fff',
                                        border: 'none',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        position: 'relative'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#7fb069';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(150, 202, 45, 0.3)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#96ca2d';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                      }}
                                      aria-label="Admin"
                                    >
                                      <i className="fas fa-user" style={{ fontSize: '1rem' }} />
                                    </button>
                                    
                                    {adminDropdownOpen && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 8px)',
                                        left: '20px',
                                        background: '#ffffff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                        minWidth: '180px',
                                        zIndex: 1000,
                                        overflow: 'hidden'
                                      }}
                                      onMouseLeave={() => setAdminDropdownOpen(false)}
                                      >
                                        <Link
                                          to="/admin/dashboard"
                                          onClick={() => {
                                            setAdminDropdownOpen(false);
                                            setMenuOpen(false);
                                          }}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '12px 16px',
                                            color: '#374151',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            transition: 'all 0.2s ease',
                                            borderBottom: '1px solid #f3f4f6'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#f9fafb';
                                            e.currentTarget.style.color = '#96ca2d';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = '#374151';
                                          }}
                                        >
                                          <i className="fas fa-tachometer-alt" style={{ fontSize: '14px', width: '16px' }} />
                                          <span>{t('navbar.dashboard')}</span>
                                        </Link>
                                        <button
                                          onClick={() => {
                                            handleLogout();
                                            setAdminDropdownOpen(false);
                                            setMenuOpen(false);
                                          }}
                                          style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '12px 16px',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#dc2626',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            textAlign: 'left',
                                            textTransform: 'none'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#fef2f2';
                                            e.currentTarget.style.color = '#991b1b';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = '#dc2626';
                                          }}
                                        >
                                          <i className="fas fa-sign-out-alt" style={{ fontSize: '14px', width: '16px' }} />
                                          <span>{t('navbar.logout')}</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                              <div className="menu-item menu-item-type-post_type menu-item-object-page jet-nav__item">
                                <Link to="/login" className="menu-item-link menu-item-link-depth-0 menu-item-link-top" onClick={() => setMenuOpen(false)}>
                                  <span className="jet-nav-link-text">{t('navbar.login')}</span>
                                </Link>
                              </div>
                              )}
                              
                              {/* Réseaux sociaux pour mobile */}
                              <div className="mobile-social-links">
                                <div className="mobile-social-title">{t('navbar.followUs')}</div>
                                <div className="mobile-social-icons">
                                  <a href="https://www.facebook.com/medagricycle" target="_blank" rel="noopener noreferrer" className="mobile-social-link facebook">
                                    <i className="fab fa-facebook-f"></i>
                                  </a>
                                  <a href="https://www.twitter.com/medagricycle" target="_blank" rel="noopener noreferrer" className="mobile-social-link twitter">
                                    <i className="fab fa-twitter"></i>
                                  </a>
                                  <a href="https://www.linkedin.com/company/medagricycle" target="_blank" rel="noopener noreferrer" className="mobile-social-link linkedin">
                                    <i className="fab fa-linkedin-in"></i>
                                  </a>
                                  <a href="https://www.instagram.com/medagricycle" target="_blank" rel="noopener noreferrer" className="mobile-social-link instagram">
                                    <i className="fab fa-instagram"></i>
                                  </a>
                                  <a href="https://www.youtube.com/@medagricycle" target="_blank" rel="noopener noreferrer" className="mobile-social-link youtube">
                                    <i className="fab fa-youtube"></i>
                                  </a>
                                </div>
                              </div>
                              
                              
                      
                              
                              <div 
                                className="jet-nav__mobile-close-btn jet-blocks-icon"
                                onClick={() => setMenuOpen(false)}
                                style={{ cursor: 'pointer', fontSize: '30px', color: 'white' }}
                              >
                                <i aria-hidden="true" className="fas fa-times" style={{fontSize:"30px",color:"white"}}></i>
                              </div>
                            </div>
                          </div>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
                
              
              </div>
            </section>
          </div>
        </div>
      </header>
      <div id="toTop" role="button" className={showToTop ? 'show' : ''} title="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}></div>
      
      {/* Bouton Top Mobile - Identique au desktop */}
      <div 
        id="mobileToTop" 
        className={`mobile-top-button ${showToTop ? 'show' : ''}`}
        title="Back to top" 
        role="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
      </div>
    </>
  );
};

export default Navbar;